import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { cn } from "@/lib/utils";

export type FormInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, "render"> & {
  label?: string;
  type?: string;
  placeholder?: string;
  description?: string;
  autoComplete?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  descriptionClassName?: string;
  messageClassName?: string;
  required?: boolean;
  disabled?: boolean;
  maxLength?: number | undefined;
  numbersOnly?: boolean;
  addOn?: React.ReactNode;
  onValueChange?: (value: string | number) => void;
  preserveMessageSpace?: boolean;
};

export const FormInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  label,
  name,
  type = "text",
  placeholder,
  autoComplete = "off",
  description,
  wrapperClassName,
  labelClassName,
  inputClassName,
  descriptionClassName,
  messageClassName,
  maxLength = undefined,
  numbersOnly,
  disabled,
  onValueChange,
  preserveMessageSpace = false,
  required,
  ...props
}: FormInputProps<TFieldValues, TName>) => {
  const stringToNumber = (value: string): number | string | undefined => {
    if (value === "") {
      return undefined;
    }
    // If the value is a string of number ( which include negative number or decimal number ), convert it into number
    if (/^-?\d*\.?\d+$/.test(value)) {
      return Number(value);
    }
    return value;
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem
          className={wrapperClassName}
          aria-invalid={!!fieldState?.error}
        >
          {label && (
            <FormLabel
              className={cn(
                labelClassName,
                required && "aero:flex aero:items-center aero:justify-between"
              )}
            >
              {label}{" "}
              {required && (
                <span className="aero:text-destructive" aria-hidden="true">
                  *
                </span>
              )}
            </FormLabel>
          )}
          <FormControl>
            <Input
              className={cn(
                "aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-foreground aero:placeholder:text-neutral-500",
                inputClassName
              )}
              placeholder={placeholder}
              type={type ?? "text"}
              // maxLength={type === "number" ? undefined : maxLength}
              // inputMode={numbersOnly ? "numeric" : undefined}
              // pattern={numbersOnly ? "[0-9]*" : undefined}
              {...field}
              value={field.value ?? ""}
              onChange={(event) => {
                if (onValueChange) {
                  onValueChange(event.target.value || "");
                }

                if (type === "text") {
                  field.onChange(event.target.value || "");
                } else {
                  field.onChange(stringToNumber(event.target.value) || "");
                }

                // let value = event.target.value;
                // // filter out non-numeric characters if numbersOnly is true
                // if (numbersOnly) {
                //   value = value.replace(/[^0-9]/g, "");
                // }
                // if (
                //   type === "number" &&
                //   maxLength &&
                //   value.length > maxLength
                // ) {
                //   value = value.slice(0, maxLength);
                // }
                // // handle maxLength for text inputs with numbersOnly
                // if (numbersOnly && maxLength && value.length > maxLength) {
                //   value = value.slice(0, maxLength);
                // }
                // if (onValueChange) {
                //   onValueChange(value || "");
                // }
                // if (type === "text") {
                //   field.onChange(value || "");
                // } else {
                //   field.onChange(stringToNumber(value) || "");
                // }
              }}
              // onKeyDown={(event) => {
              //   // Prevent non-numeric keys if numbersOnly is true
              //   if (numbersOnly) {
              //     // Allow: backspace, delete, tab, escape, enter, and navigation keys
              //     if (
              //       [8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
              //       // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
              //       (event.keyCode === 65 && event.ctrlKey === true) ||
              //       (event.keyCode === 67 && event.ctrlKey === true) ||
              //       (event.keyCode === 86 && event.ctrlKey === true) ||
              //       (event.keyCode === 88 && event.ctrlKey === true) ||
              //       // Allow home, end, left, right, up, down
              //       (event.keyCode >= 35 && event.keyCode <= 40)
              //     ) {
              //       return; // let it happen, don't do anything
              //     }
              //     // Ensure that it's a number and stop the keypress
              //     if (
              //       (event.shiftKey ||
              //         event.keyCode < 48 ||
              //         event.keyCode > 57) &&
              //       (event.keyCode < 96 || event.keyCode > 105)
              //     ) {
              //       event.preventDefault();
              //     }
              //   }
              // }}
              // onInput={(event) => {
              //   if (type === "number" && maxLength) {
              //     const target = event.target as HTMLInputElement;
              //     if (target.value.length > maxLength) {
              //       target.value = target.value.slice(0, maxLength);
              //     }
              //   }
              // }}
              autoComplete={autoComplete}
              disabled={disabled}
            />
          </FormControl>
          {description && (
            <FormDescription className={descriptionClassName}>
              {description}
            </FormDescription>
          )}
          <div className={cn(preserveMessageSpace ? "aero:min-h-[18px]" : "")}>
            <FormMessage
              className={cn(
                fieldState?.error
                  ? "aero:text-destructive aero:text-xs"
                  : preserveMessageSpace
                    ? "aero:invisible aero:text-xs"
                    : "aero:hidden",
                messageClassName
              )}
            />
          </div>
        </FormItem>
      )}
      {...props}
    />
  );
};
